const { BESTEK_LINE_REQUIREMENT } = require("../types/bestek.types");

const toNumber = (value) => {
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  if (value == null || value === "") return 0;
  const n = Number(String(value).replace(",", "."));
  return Number.isFinite(n) ? n : 0;
};

const lineTotal = (line = {}) =>
  toNumber(line.total ?? line.RAMUTOT ?? line.RAMRPMTOT);

const getEstimateLineId = (line = {}) => {
  if (line._id != null && line._id !== "") return String(line._id);
  if (line.id != null && line.id !== "") return String(line.id);
  if (line.isReplacementHost === true) return "replacement-host";
  if (line.isExecutedRepair === true) return "executed-repair";
  if (line.isSupplierTask === true && line.sourceSupplierTaskHTOBJ != null) {
    return `supplier-task-${line.sourceSupplierTaskHTOBJ}`;
  }
  if (line.sourceRAMUOBJ != null) return String(line.sourceRAMUOBJ);
  if (line.lineNr != null) return `line-${line.lineNr}`;
  return "";
};

const emptyMaterialLine = () => ({
  description: "",
  artNr: "",
  quantity: 1,
  unit: "stk",
  unitPrice: 0,
  total: 0,
  stockTotal: null,
  sourceLineNr: null,
  sourceRAMRPUOBJ: null,
});

const emptyProductOption = () => ({
  description: "",
  artNr: "",
  unitPrice: 0,
  unit: "stk",
  stockTotal: null,
});

const emptyRaming = () => ({
  sourceRAMUOBJ: null,
  lineNr: null,
  description: "",
  internalDescription: "",
  technician: "",
  timeSeconds: 0,
  unitPrice: 0,
  total: 0,
  type: "",
  lineRequirement: BESTEK_LINE_REQUIREMENT.MOET,
  materialLines: [],
  newProductOptions: [],
});

/** Hidden raming that only carries the vervangartikel (no FinERP raming). */
const isReplacementHostRaming = (raming = {}) =>
  raming?.isReplacementHost === true;

const getRamingTotal = (raming = {}) => {
  const labor = lineTotal(raming);
  const materials = (raming.materialLines || []).reduce(
    (sum, line) => sum + lineTotal(line),
    0
  );
  return Math.round((labor + materials) * 100) / 100;
};

const normalizeRamingShape = (raming = {}) => ({
  ...emptyRaming(),
  ...raming,
  lineRequirement: raming.lineRequirement || BESTEK_LINE_REQUIREMENT.MOET,
  materialLines: (raming.materialLines || []).map((line) => ({
    ...emptyMaterialLine(),
    ...line,
  })),
  newProductOptions: (raming.newProductOptions || []).map((line) => ({
    ...emptyProductOption(),
    ...line,
  })),
});

const migrateFlatLinesToRamingen = (bestek = {}) => {
  const laborLines = bestek.repairLaborLines || [];
  const materialLines = bestek.repairMaterialLines || [];
  if (laborLines.length === 0 && materialLines.length === 0) {
    return [];
  }

  const ramingen = laborLines.map((line) =>
    normalizeRamingShape({
      ...line,
      materialLines: materialLines.filter(
        (mat) =>
          mat.sourceRAMRPUOBJ != null &&
          line.sourceRAMUOBJ != null &&
          Number(mat.sourceRAMRPUOBJ) === Number(line.sourceRAMUOBJ)
      ),
      newProductOptions: [],
    })
  );

  const linkedMaterialKeys = new Set(
    ramingen.flatMap((r) =>
      (r.materialLines || []).map(
        (m) => `${m.sourceRAMRPUOBJ}-${m.sourceLineNr}-${m.description}`
      )
    )
  );

  const orphanMaterials = materialLines.filter((mat) => {
    const key = `${mat.sourceRAMRPUOBJ}-${mat.sourceLineNr}-${mat.description}`;
    if (linkedMaterialKeys.has(key)) return false;
    if (mat.sourceRAMRPUOBJ == null) return true;
    return !ramingen.some(
      (r) => Number(r.sourceRAMUOBJ) === Number(mat.sourceRAMRPUOBJ)
    );
  });

  for (const mat of orphanMaterials) {
    const byLineNr = ramingen.find(
      (r) => r.lineNr != null && Number(r.lineNr) === Number(mat.sourceLineNr)
    );
    if (byLineNr) {
      byLineNr.materialLines.push({ ...emptyMaterialLine(), ...mat });
    } else if (ramingen.length > 0) {
      ramingen[0].materialLines.push({ ...emptyMaterialLine(), ...mat });
    } else {
      ramingen.push(
        normalizeRamingShape({
          description: mat.description || "Materialen",
          materialLines: [{ ...emptyMaterialLine(), ...mat }],
        })
      );
    }
  }

  const legacyProducts = bestek.newMachineOptions || [];
  if (legacyProducts.length > 0) {
    if (ramingen.length === 0) {
      ramingen.push(emptyRaming());
    }
    ramingen[0].newProductOptions = legacyProducts.map((p) => ({
      ...emptyProductOption(),
      ...p,
    }));
  }

  return ramingen;
};

/** Read ramingen from bestek, migrating legacy flat arrays when needed. */
const getRepairRamingen = (bestek = {}) => {
  if (Array.isArray(bestek.repairRamingen) && bestek.repairRamingen.length > 0) {
    return bestek.repairRamingen.map(normalizeRamingShape);
  }
  return migrateFlatLinesToRamingen(bestek);
};

/** Persist only grouped ramingen (strip legacy flat arrays). */
const serializeBestekRamingen = (bestek = {}) => {
  const repairRamingen = getRepairRamingen(bestek);
  const next = { ...bestek, repairRamingen };
  delete next.repairLaborLines;
  delete next.repairMaterialLines;
  delete next.newMachineOptions;
  return next;
};

const collectAllNewProductOptions = (bestek = {}) =>
  getRepairRamingen(bestek).flatMap((raming) =>
    (raming.newProductOptions || []).map((product) => ({
      ...product,
      ramingId: getEstimateLineId(raming),
      ramingDescription: raming.description || "",
    }))
  );

const findNewProductOption = (bestek, { ramingId, productId }) => {
  for (const raming of getRepairRamingen(bestek)) {
    if (ramingId && String(getEstimateLineId(raming)) !== String(ramingId)) {
      continue;
    }
    const product =
      (raming.newProductOptions || []).find(
        (p) => String(p._id || p.id) === String(productId)
      ) || (raming.newProductOptions || [])[0];
    if (product) {
      return {
        raming,
        product,
        ramingId: getEstimateLineId(raming),
        productId: String(product._id || product.id),
      };
    }
  }
  return null;
};

module.exports = {
  emptyMaterialLine,
  emptyProductOption,
  emptyRaming,
  getRamingTotal,
  getRepairRamingen,
  isReplacementHostRaming,
  normalizeRamingShape,
  migrateFlatLinesToRamingen,
  serializeBestekRamingen,
  collectAllNewProductOptions,
  findNewProductOption,
  getEstimateLineId,
};
